import React, { useState, useRef, useCallback, useMemo } from 'react'
import { useTable } from 'react-table'
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

import Button from 'components/v1/Button';
import ModalDetailBayarTraining from "./detail-bayar-training"

import { useGetInvoiceTraining } from 'api/training/query';
import { useCreateTagihan } from 'api/training/mutation';
import { useGlobalCtx } from 'contexts';

// Column styles are now managed within the column definitions

const CStatus = ({ status }) => {
  switch (status) {
    case "waiting": return <span className='bg-[#F6E5B6] rounded-2xl py-0.5 px-2 text-[16px]'>Belum Bayar</span>
    case "paid": return <span className='bg-[#ACE2C8] rounded-2xl px-2 py-0.5 text-[16px]'>Lunas</span>
    case "waiting_payment": return <span className='bg-[#E6E7E8] rounded-2xl px-2 py-0.5 text-[16px]'>Menunggu Pembayaran</span>
    case "failed": return <span className='bg-[#FD4949] text-white py-0.5 rounded-2xl px-2 text-[16px]'>Gagal</span>
    default: return null
  }
}

// These components are now handled by react-table

const ListTraining = () => {
  const locaiton = useLocation()
  const isTraining = locaiton.pathname === "/invoice/training"
  const isListing = locaiton.pathname === "/invoice"

  const { invoiceTrainingCounter, invoiceListingCounter } = useGlobalCtx()

  const [selectedInvoice, setSelectedInvoice] = useState(null)
  const [isOpen, setIsOpen] = useState(false)

  const [params, setParams] = useState({
    billing_status: "waiting",
    _limit: 30
  })

  // Choose which result to use based on useMockData
  const { data: listInvoice, fetchNextPage, hasNextPage, isFetchingNextPage, status } = useGetInvoiceTraining({ params })

  // Prepare data for react-table
  const tableData = useMemo(() => {
    if (status !== 'success' || !listInvoice?.pages) return []

    return listInvoice.pages.flatMap((page, pageIndex) =>
      page?.data?.data?.training_billing?.map((invoice, idx) => {
        const invoiceNumber = pageIndex > 0
          ? listInvoice.pages[pageIndex - 1].data.data.training_billing.length * pageIndex + idx + 1
          : idx + 1

        // Check if this is the last element for infinite scrolling
        const isLastElement = pageIndex === listInvoice.pages.length - 1 &&
          idx === page.data.data.training_billing.length - 1

        // Add index and ref flag to data
        return {
          ...invoice,
          index: invoiceNumber,
          isLastElement
        }
      })
    ) || []
  }, [listInvoice, status])

  // Define columns for react-table
  const columns = useMemo(
    () => [
      {
        Header: 'No',
        accessor: 'index',
        width: 80
      },
      {
        Header: 'Training',
        accessor: 'training_batch',
        width: 300
      },
      {
        Header: 'Tagihan',
        accessor: 'amount',
        Cell: ({ value }) => parseInt(value)?.toLocaleString("id-ID"),
        width: 196
      },
      {
        Header: 'Tanggal Invoice',
        accessor: 'invoice_date',
        Cell: ({ value }) => dayjs(value)?.format("DD-MM-YYYY"),
        width: 196
      },
      {
        Header: 'Status Pembayaran',
        accessor: 'billing_status',
        Cell: ({ value }) => <CStatus status={value} />,
        width: 213.5
      },
      {
        Header: '',
        accessor: 'action',
        Cell: ({ row }) => {
          const invoice = row.original;
          if (invoice?.billing_status === "waiting") {
            return (
              <Button
                onClick={() => {
                  setSelectedInvoice(invoice)
                  setIsOpen(true)
                }}
                text="Detail"
                size="xs"
              />
            );
          } else if (invoice?.billing_status === "waiting_payment") {
            return (
              <Button
                onClick={() => {
                  window.open(invoice?.payment_url, "_blank")
                }}
                text="Bayar"
                size="xs"
              />
            );
          }
          return null;
        },
        width: 50
      }
    ],
    [])

  // Use react-table at the top level of component
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow
  } = useTable({ columns, data: tableData })

  // Create an observer for infinite scrolling
  const observer = useRef()
  const lastInvoiceElementRef = useCallback(
    (node) => {
      if (isFetchingNextPage || !hasNextPage) return
      if (observer.current) observer.current.disconnect()
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage()
        }
      })
      if (node) observer.current.observe(node)
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  )

  const { mutate: mutateCreateTagihan } = useCreateTagihan()

  const handleCreateTagihan = (id, voucher_discount) => {
    mutateCreateTagihan({ id, voucher_discount }, {
      onSuccess: (response) => {
        const url = response?.data?.data?.payment_url ?? "";
        setIsOpen(false)
        if (url) {
          window.open(url, "_blank")
        }
      },
      onError: (error) => {
        toast.error(error?.message ?? "Gagal membuat tagihan")
      }
    })
  }

  return (
    <div className='w-[100vw-80px] bg-[#D8D8D9]'>
      <div className='py-[14px] px-6 bg-white'>
        <p className='text-[22px] font-semibold leading-[35.596px] tracking-[-0.4px]'>Invoice</p>
        <div className='w-[310px] h-[0.5px] border-solid border-b-[#D8D8D9] my-0.5' />
        <p className='text-[13px] text-[#808285] leading-[35.596px] tracking-[-0.4px]'>Kelola invoice kantor yang masuk.</p>
      </div>
      <div className='p-6 bg-[#D8D8D9]'>
        <div className='rounded bg-white'>
          <div className='px-4 pt-2 rounded-t flex items-center border-b-[0.5px] border-solid border-[#D8D8D9]'>
            <Link to="/invoice">
              <div className={`cursor-pointer px-2 py-1 text-[13px] text-[#808285] flex items-center ${isListing ? "!border-b-[2px] border-solid border-[#252526]" : ""}`}>
                <span className='text-[17px] text-[#808285]'>Royalti</span>
                {!!(invoiceListingCounter > 0) && <span className={`ml-1 rounded-[2px] py-0.2 px-[3.5px] bg-[#FD4949] text-[11px] text-white flex items-center justify-center`}>{invoiceListingCounter}</span>}
              </div>
            </Link>
            <div className={`cursor-pointer px-2 py-1 text-[13px] text-[#808285] flex items-center ${isTraining ? "!border-b-[2px] border-solid border-[#252526]" : ""}`}>
              <span className='text-[17px] text-[#808285]'>Training</span>
              {!!(invoiceTrainingCounter > 0) && <span className={`ml-1 rounded-[2px] py-0.2 px-[3.5px] bg-[#FD4949] text-[11px] text-white flex items-center justify-center`}>{invoiceTrainingCounter}</span>}
            </div>
          </div>


          <div className='px-4 pt-4 flex justify-between'>
            <div className='flex items-center bg-[#E6E7E8] p-0.5 w-fit rounded'>
              <button onClick={() => setParams({ ...params, billing_status: "waiting" })} className={`text-[14px] whitespace-nowrap  ${params.billing_status === "waiting" ? "text-[#252526] font-semibold bg-white shadow-[0px_0px_2px_0px_rgba(0,0,0,0.32)]" : "text-[#808285] bg-[#E6E7E8]"} w-[100px] flex items-center justify-center py-1 px-2 rounded`}>
                Belum Bayar
              </button>
              <button onClick={() => setParams({ ...params, billing_status: "paid" })} className={`text-[14px] whitespace-nowrap  ${params.billing_status === "paid" ? "text-[#252526] font-semibold bg-white shadow-[0px_0px_2px_0px_rgba(0,0,0,0.32)]" : "text-[#808285] bg-[#E6E7E8]"} w-[100px] flex items-center justify-center py-1 px-2 rounded`}>
                Sudah Bayar
              </button>
            </div>
          </div>


          <div className='p-4'>
            <div className='border-[0.5px] border-solid border-[#D8D8D9] rounded bg-[#FAFAFA]'>
              {status === 'success' && (
                <table {...getTableProps()} className="w-full">
                  <thead>
                    {headerGroups.map(headerGroup => (
                      <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()} className='border-b-[0.5px] border-solid border-[#D8D8D9]'>
                        {headerGroup.headers.map(column => (
                          <th
                            key={column.id}
                            {...column.getHeaderProps()}
                            className='px-4 py-2 text-left text-[14px] text-[#808285]'
                            style={{ width: column.width }}
                          >
                            {column.render('Header')}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...getTableBodyProps()}>
                    {rows.map(row => {
                      prepareRow(row)
                      return (
                        <tr
                          key={row.id}
                          {...row.getRowProps()}
                          className='border-b-[0.5px] border-solid border-[#D8D8D9] last:border-b-0'
                          ref={row.original?.isLastElement ? lastInvoiceElementRef : null}
                        >
                          {row.cells.map(cell => {
                            return (
                              <td
                                key={cell.column.id}
                                {...cell.getCellProps()}
                                className='px-4 py-[10px] text-[16px] text-[#101010] text-truncate'
                                style={{ width: cell.column.width }}
                              >
                                {cell.render('Cell')}
                              </td>
                            )
                          })}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}

              {isFetchingNextPage && (
                <div className='px-4 py-4 flex justify-center'>
                  <p className='text-[14px] text-[#808285]'>Memuat data...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ModalDetailBayarTraining
        isOpen={isOpen}
        onRequestClose={() => setIsOpen(false)}
        onSubmit={handleCreateTagihan}
        selectedInvoice={selectedInvoice}
      />
    </div>
  )
}

export default ListTraining